import { useEffect, useRef, useState } from "react";

export default function Reveal({ children, delay = 0, className = "", as: Tag = "div" }) {
    const ref = useRef(null);
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;

        // Fallback: if IntersectionObserver is unavailable, show immediately
        if (typeof IntersectionObserver === "undefined") {
            setVisible(true);
            return;
        }

        let timeoutId;
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        timeoutId = setTimeout(() => setVisible(true), delay);
                        observer.unobserve(entry.target);
                    }
                });
            },
            { threshold: 0.05, rootMargin: "0px 0px -40px 0px" }
        );
        observer.observe(el);

        // Safety net: if observer hasn't fired within 1.2s + delay (e.g. element already on screen
        // but a strict-mode double-mount disconnected the first observer at an awkward moment),
        // reveal anyway so content is never stuck invisible.
        const safety = setTimeout(() => setVisible(true), 1200 + delay);

        return () => {
            observer.disconnect();
            if (timeoutId) clearTimeout(timeoutId);
            clearTimeout(safety);
        };
    }, [delay]);

    return (
        <Tag ref={ref} className={`reveal ${visible ? "is-visible" : ""} ${className}`}>
            {children}
        </Tag>
    );
}
